import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Plus, Trash2, X, Edit2, Save, Link as LinkIcon } from 'lucide-react';
import useRestaurants from '../../hooks/useRestaurants';
import { useSettings } from '../../contexts/SettingsContext';
import { updateRestaurant } from '../../api/restaurantsApi';

/**
 * VoiceAgentTab Component
 * 
 * Configures AI behavior, tone, voice settings, and knowledge base.
 */
const VoiceAgentTab = () => {
  const { restaurants, isLoading: isLoadingRestaurants, refetch } = useRestaurants();
  const { selectedLocationId, setSelectedLocationId } = useSettings();

  // Auto-select first location if "all" is selected (Voice Agent tab needs a specific location)
  useEffect(() => {
    if (selectedLocationId === 'all' && restaurants && restaurants.length > 0 && !isLoadingRestaurants) {
      setSelectedLocationId(restaurants[0].locationId);
    }
  }, [selectedLocationId, restaurants, isLoadingRestaurants, setSelectedLocationId]);

  // Greeting State
  const [greetingMessage, setGreetingMessage] = useState('Hi, thanks for calling. How can I help you today?');
  const [isEditingGreeting, setIsEditingGreeting] = useState(false);
  const [originalGreeting, setOriginalGreeting] = useState('');

  // Links State
  const [links, setLinks] = useState([]);
  const [isEditingLinks, setIsEditingLinks] = useState(false);
  const [originalLinks, setOriginalLinks] = useState([]);
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLink, setNewLink] = useState({ name: '', url: '' });

  // Menu & Knowledge State - Commented out for now
  // const [menuFile, setMenuFile] = useState(null);
  // const [websiteUrl, setWebsiteUrl] = useState('');
  // const [faqs, setFaqs] = useState([
  //   { id: 1, question: 'Do you offer catering?', answer: 'Yes, we cater events of all sizes. Please call 703-555-2040 for details.' }
  // ]);
  // const [newFaq, setNewFaq] = useState({ question: '', answer: '' });
  // const [showAddFaq, setShowAddFaq] = useState(false);
  // const fileInputRef = useRef(null);

  // Convert camelCase/snake_case to Title Case
  const formatLinkName = (fieldName) => {
    // Handle camelCase: orderLink -> Order Link
    // Handle snake_case: order_link -> Order Link
    return fieldName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Recursively find all link fields in an object
  const findLinkFields = useCallback((obj, prefix = '', foundLinks = []) => {
    if (!obj || typeof obj !== 'object') return foundLinks;

    for (const [key, value] of Object.entries(obj)) {
      // Skip certain fields that aren't links
      if (key === 'links' || key === 'customLinks' || key === 'locationId' || key === 'agentId') {
        continue;
      }

      // Check if field name contains "link" (case insensitive)
      const isLinkField = /link/i.test(key);
      
      if (isLinkField && typeof value === 'string' && value.trim() !== '') {
        // It's a link field with a URL value
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const displayName = formatLinkName(key);
        foundLinks.push({
          id: fullKey,
          name: displayName,
          url: value,
          isCustom: false,
          fieldPath: fullKey
        });
      } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Recursively search nested objects
        const newPrefix = prefix ? `${prefix}.${key}` : key;
        findLinkFields(value, newPrefix, foundLinks);
      }
    }

    return foundLinks;
  }, []);

  // Load restaurant data when selection changes
  useEffect(() => {
    if (restaurants && restaurants.length > 0 && selectedLocationId) {
      const restaurant = restaurants.find(r => r.locationId === selectedLocationId);
      if (restaurant) {

        // Load greeting message from DynamoDB
        setGreetingMessage(restaurant.greetingPhrase || '');
        setOriginalGreeting(restaurant.greetingPhrase || '');

        // Dynamically find all link fields from restaurant data
        const loadedLinks = findLinkFields(restaurant);
        
        // Also load from links array if it exists (custom links)
        if (restaurant.links && Array.isArray(restaurant.links)) {
          restaurant.links.forEach((link, index) => {
            loadedLinks.push({ 
              id: `link-${index}`, 
              name: link.name || 'Custom Link', 
              url: link.url, 
              isCustom: true 
            });
          });
        }
        // Also check customLinks for backwards compatibility
        if (restaurant.customLinks && Array.isArray(restaurant.customLinks)) {
          restaurant.customLinks.forEach((link, index) => {
            loadedLinks.push({ 
              id: `custom-${index}`, 
              name: link.name || 'Custom Link', 
              url: link.url, 
              isCustom: true 
            });
          });
        }

        setLinks(loadedLinks);
        setOriginalLinks(JSON.parse(JSON.stringify(loadedLinks)));
      }
    }
  }, [restaurants, selectedLocationId, findLinkFields]);

  // Handle menu file upload - Commented out for now
  // const handleMenuUpload = (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     // Accept PDF, DOC, DOCX files
  //     const validTypes = [
  //       'application/pdf',
  //       'application/msword',
  //       'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  //       'text/csv'
  //     ];
  //     const validExtensions = ['.pdf', '.doc', '.docx', '.csv'];
  //     const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  //     
  //     if (validTypes.includes(file.type) || validExtensions.includes(fileExtension)) {
  //       setMenuFile(file);
  //       alert(`Menu file "${file.name}" uploaded successfully!`);
  //     } else {
  //       alert('Please upload a PDF, DOC, DOCX, or CSV file');
  //     }
  //   }
  // };

  // Trigger file input click - Commented out for now
  // const handleUploadClick = () => {
  //   fileInputRef.current?.click();
  // };

  // Handle greeting edit
  const handleEditGreeting = () => {
    setOriginalGreeting(greetingMessage);
    setIsEditingGreeting(true);
  };

  const handleSaveGreeting = async () => {
    if (!selectedLocationId) return;
    
    setIsEditingGreeting(false);
    try {
      // Send empty string if greeting is cleared, otherwise send the message
      await updateRestaurant(selectedLocationId, { 
        greetingPhrase: greetingMessage || '' 
      });
      console.log('Greeting saved successfully:', greetingMessage || '(empty)');
      
      // Refresh restaurant data to get latest from DynamoDB
      if (refetch) {
        await refetch();
      }
    } catch (error) {
      console.error('Error saving greeting:', error);
      alert(error.message || 'Failed to save greeting. Please try again.');
      setIsEditingGreeting(true);
    }
  };

  const handleCancelGreeting = () => {
    setGreetingMessage(originalGreeting);
    setIsEditingGreeting(false);
  };

  // Handle links operations
  const handleEditLinks = () => {
    setOriginalLinks(JSON.parse(JSON.stringify(links)));
    setIsEditingLinks(true);
  };

  const handleSaveLinks = async () => {
    if (!selectedLocationId) return;
    
    setIsEditingLinks(false);
    setOriginalLinks(JSON.parse(JSON.stringify(links)));

    try {
      // Get current restaurant to compare what was deleted
      const currentRestaurant = restaurants.find(r => r.locationId === selectedLocationId);
      
      // Separate custom links from field-based links
      const customLinks = links.filter(link => link.isCustom);
      const fieldLinks = links.filter(link => !link.isCustom && link.fieldPath);

      // Build update object with field-based links
      const updates = {};
      
      // Save custom links in the links array (or empty string if none)
      if (customLinks.length > 0) {
        updates.links = customLinks.map(link => ({
          name: link.name,
          url: link.url
        }));
      } else if (currentRestaurant?.links) {
        // If there were links before but now there are none, send empty string
        updates.links = '';
      }

      // Track which field-based links exist in current state
      const currentFieldPaths = new Set(fieldLinks.map(link => link.fieldPath));
      
      // Find original field-based links to detect deletions
      const originalFieldLinks = originalLinks.filter(link => !link.isCustom && link.fieldPath);
      
      // For each original field link, check if it was deleted
      originalFieldLinks.forEach(originalLink => {
        if (!currentFieldPaths.has(originalLink.fieldPath)) {
          // Link was deleted - send empty string
          const pathParts = originalLink.fieldPath.split('.');
          if (pathParts.length === 1) {
            updates[originalLink.fieldPath] = '';
          } else {
            const parentField = pathParts[0];
            const childField = pathParts[1];
            const parentValue = currentRestaurant?.[parentField] || {};
            updates[parentField] = {
              ...parentValue,
              [childField]: ''
            };
          }
        }
      });

      // Save field-based links to their respective fields
      fieldLinks.forEach(link => {
        // Handle nested paths like "orderLinks.pickupLink"
        const pathParts = link.fieldPath.split('.');
        if (pathParts.length === 1) {
          // Simple field: orderLink
          updates[link.fieldPath] = link.url || '';
        } else {
          // Nested field: orderLinks.pickupLink
          const parentField = pathParts[0];
          const childField = pathParts[1];
          
          // Get existing parent object or create new one
          const parentValue = currentRestaurant?.[parentField] || {};
          updates[parentField] = {
            ...parentValue,
            [childField]: link.url || ''
          };
        }
      });

      await updateRestaurant(selectedLocationId, updates);
      console.log('Links saved successfully');
      
      // Refresh restaurant data to get latest from DynamoDB
      if (refetch) {
        await refetch();
      }
    } catch (error) {
      console.error('Error saving links:', error);
      alert(error.message || 'Failed to save links. Please try again.');
      setIsEditingLinks(true);
    }
  };

  const handleCancelLinks = () => {
    setLinks(JSON.parse(JSON.stringify(originalLinks)));
    setIsEditingLinks(false);
  };

  const handleAddLink = () => {
    if (newLink.name && newLink.url) {
      setLinks([...links, { id: `custom-${Date.now()}`, name: newLink.name, url: newLink.url, isCustom: true }]);
      setNewLink({ name: '', url: '' });
      setShowAddLink(false);
    }
  };

  const handleDeleteLink = (id) => {
    setLinks(links.filter(link => link.id !== id));
  };

  const handleUpdateLink = (id, field, value) => {
    setLinks(links.map(link => 
      link.id === id ? { ...link, [field]: value } : link
    ));
  };

  // Handle FAQ operations - Commented out for now
  // const handleAddFaq = () => {
  //   if (newFaq.question && newFaq.answer) {
  //     setFaqs([...faqs, { id: Date.now(), ...newFaq }]);
  //     setNewFaq({ question: '', answer: '' });
  //     setShowAddFaq(false);
  //   }
  // };

  // const handleDeleteFaq = (id) => {
  //   setFaqs(faqs.filter(faq => faq.id !== id));
  // };

  if (isLoadingRestaurants) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3AD0E6]" />
      </div>
    );
  }

  if (!selectedLocationId) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-400">Please select a location in the Organization tab first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Greeting Section */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Greeting</h3>
              <p className="text-sm text-gray-400">Customize how the AI greets callers</p>
            </div>
            {!isEditingGreeting && (
              <Button
                onClick={handleEditGreeting}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-gray-700"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
            )}
            {isEditingGreeting && (
              <div className="flex gap-2">
                <Button
                  onClick={handleCancelGreeting}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white hover:bg-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
                <Button
                  onClick={handleSaveGreeting}
                  size="sm"
                  className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                >
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Greeting Message
            </label>
            {isEditingGreeting ? (
              <textarea
                value={greetingMessage}
                onChange={(e) => setGreetingMessage(e.target.value)}
                rows={3}
                className="flex w-full rounded-md border border-gray-700 bg-gray-900 px-3 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3AD0E6] focus-visible:ring-offset-2"
                placeholder="e.g., Hi, thanks for calling Bella's Pizzeria. How can I help you today?"
              />
            ) : (
              <div className="p-3 bg-gray-900 rounded-md border border-gray-700 min-h-[72px]">
                <p className="text-sm text-white whitespace-pre-wrap">
                  {greetingMessage || 'No greeting message set'}
                </p>
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1">
              This message will be spoken when calls are answered
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Links Section */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Links</h3>
              <p className="text-sm text-gray-400">Manage links the AI can share with callers</p>
            </div>
            {!isEditingLinks && (
              <Button
                onClick={handleEditLinks}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-gray-700"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
            {isEditingLinks && (
              <div className="flex gap-2">
                <Button
                  onClick={handleCancelLinks}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white hover:bg-gray-700"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveLinks}
                  size="sm"
                  className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {links.length === 0 && !isEditingLinks ? (
            <p className="text-sm text-gray-400 italic">No links configured for this location</p>
          ) : (
            <div className="space-y-3">
              {links.map((link) => (
                <div
                  key={link.id}
                  className="p-4 bg-gray-900 rounded-lg border border-gray-700"
                >
                  <div className="flex items-start gap-3">
                    <LinkIcon className="w-5 h-5 text-[#3AD0E6] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      {isEditingLinks ? (
                        <>
                          <Input
                            type="text"
                            value={link.name}
                            onChange={(e) => handleUpdateLink(link.id, 'name', e.target.value)}
                            placeholder="Link name"
                            className="bg-gray-800"
                          />
                          <Input
                            type="url"
                            value={link.url}
                            onChange={(e) => handleUpdateLink(link.id, 'url', e.target.value)}
                            placeholder="https://example.com"
                            className="bg-gray-800"
                          />
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-white">{link.name}</p>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#3AD0E6] hover:text-[#3AD0E6]/80 hover:underline break-all"
                          >
                            {link.url}
                          </a>
                        </>
                      )}
                    </div>
                    {isEditingLinks && (
                      <button
                        type="button"
                        onClick={() => handleDeleteLink(link.id)}
                        className="text-gray-400 hover:text-red-400 flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {isEditingLinks && (
            <>
              {showAddLink ? (
                <div className="p-4 bg-gray-900 rounded-lg border border-gray-700 space-y-3">
                  <Input
                    type="text"
                    placeholder="Link name (e.g., Menu Link, Catering Link)"
                    value={newLink.name}
                    onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                    className="bg-gray-800"
                  />
                  <Input
                    type="url"
                    placeholder="https://example.com"
                    value={newLink.url}
                    onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                    className="bg-gray-800"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAddLink}
                      className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                    >
                      Add
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowAddLink(false);
                        setNewLink({ name: '', url: '' });
                      }}
                      className="border-gray-700 text-gray-300 hover:bg-gray-700"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddLink(true)}
                  className="w-full border-gray-700 text-gray-300 hover:bg-gray-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Link
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Menu & Knowledge Section - Hidden for now */}
      {/* <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <h3 className="text-lg font-semibold text-white">Menu & Knowledge</h3>
          <p className="text-sm text-gray-400">Provide information the AI can reference</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Menu File
            </label>
            <div className="flex items-center gap-3">
              {menuFile ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-900 rounded border border-gray-700">
                  <span className="text-sm text-gray-300">{menuFile.name}</span>
                  <button
                    type="button"
                    onClick={() => setMenuFile(null)}
                    className="text-gray-400 hover:text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.csv"
                    onChange={handleMenuUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleUploadClick}
                    className="border-gray-700 text-gray-300 hover:bg-gray-700"
                  >
                    Upload Menu (PDF/DOC)
                  </Button>
                </>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Upload your menu (PDF, DOC, DOCX, or CSV) so the AI can answer questions about items and prices
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sync from Website URL (Optional)
            </label>
            <Input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://your-business.com/menu"
            />
            <p className="text-xs text-gray-400 mt-1">
              Automatically sync menu information from your website
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-300">
                Frequently Asked Questions
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-gray-700 text-gray-300 hover:bg-gray-700"
                onClick={() => setShowAddFaq(!showAddFaq)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add FAQ
              </Button>
            </div>

            {showAddFaq && (
              <div className="mb-4 p-4 bg-gray-900 rounded-lg border border-gray-700 space-y-3">
                <Input
                  type="text"
                  placeholder="Question"
                  value={newFaq.question}
                  onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                />
                <textarea
                  placeholder="Answer"
                  value={newFaq.answer}
                  onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                  rows={2}
                  className="flex w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3AD0E6] focus-visible:ring-offset-2"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAddFaq}
                    className="bg-[#3AD0E6] hover:bg-[#3AD0E6]/80 text-white"
                  >
                    Add
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowAddFaq(false);
                      setNewFaq({ question: '', answer: '' });
                    }}
                    className="border-gray-700 text-gray-300 hover:bg-gray-700"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {faqs.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No FAQs added yet</p>
              ) : (
                faqs.map((faq) => (
                  <div
                    key={faq.id}
                    className="p-4 bg-gray-900 rounded-lg border border-gray-700"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white mb-1">
                          Q: {faq.question}
                        </p>
                        <p className="text-sm text-gray-300">
                          A: {faq.answer}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteFaq(faq.id)}
                        className="text-gray-400 hover:text-red-400 ml-4"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
};

export default VoiceAgentTab;

